import { useState, useEffect } from 'react';
import { useHybridAuth } from '../HybridAuthProvider';
import axios from 'axios';

/**
 * TeamsHello - Teams Tab SSO Demo Component
 *
 * This component demonstrates Teams iframe SSO integration.
 * Teams detection and SSO token acquisition is now handled automatically
 * by HybridAuthProvider - this page just uses useHybridAuth() like any other page.
 */
const TeamsHello = () => {
  const { getAccessToken, isAuthenticated, login, idpType, user } = useHybridAuth();
  const [apiData, setApiData] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [loadingApi, setLoadingApi] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated) {
        setLoadingApi(false);
        return;
      }

      try {
        const token = await getAccessToken();
        if (!token) {
          setApiError('No token available');
          setLoadingApi(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/auth/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setApiData(response.data);
      } catch (err: any) {
        console.error('API error:', err);
        setApiError(err.response?.data?.detail || err.message);
      } finally {
        setLoadingApi(false);
      }
    };

    fetchUserData();
  }, [isAuthenticated, getAccessToken]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: 'Segoe UI, sans-serif',
    padding: '20px'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '32px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    textAlign: 'center',
    maxWidth: '500px',
    width: '100%'
  };

  const infoBoxStyle: React.CSSProperties = {
    backgroundColor: '#f0f4f8',
    borderRadius: '6px',
    padding: '16px',
    textAlign: 'left',
    marginTop: '16px'
  };

  const apiResponseStyle: React.CSSProperties = {
    backgroundColor: '#e8f5e9',
    borderRadius: '6px',
    padding: '16px',
    textAlign: 'left',
    marginTop: '16px',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '300px'
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: '#ffebee',
    borderRadius: '6px',
    padding: '16px',
    marginTop: '16px',
    color: '#c62828'
  };

  const isInTeams = idpType === 'teams_sso';

  if (loadingApi) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p>Loading...</p>
          <p style={{ fontSize: '12px', color: '#666' }}>
            {isInTeams ? 'Authenticating via Teams SSO...' : 'Checking authentication...'}
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login (won't happen in Teams since SSO is automatic)
  if (!isAuthenticated) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ color: '#0078D4', marginBottom: '16px' }}>Teams Hello World</h2>
          <p style={{ marginBottom: '24px' }}>Please sign in to continue</p>
          <button
            onClick={() => login()}
            style={{
              backgroundColor: '#0078D4',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{ color: '#0078D4', marginBottom: '8px' }}>Hello from HabiTrack!</h1>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          {isInTeams ? 'Running inside Microsoft Teams' : 'Running in browser'}
        </p>

        <div style={infoBoxStyle}>
          <p><strong>Auth Source:</strong> {idpType}</p>
          <p><strong>In Teams:</strong> {isInTeams ? 'Yes' : 'No'}</p>
          {user && (
            <p><strong>User:</strong> {user.email || user.name || 'N/A'}</p>
          )}
        </div>

        {apiError && (
          <div style={errorStyle}>
            <strong>API Error:</strong> {apiError}
          </div>
        )}

        {apiData && (
          <div style={apiResponseStyle}>
            <strong>API Response (auth/me):</strong>
            <pre style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(apiData, null, 2)}
            </pre>
          </div>
        )}

        <p style={{ marginTop: '24px', fontSize: '12px', color: '#888' }}>
          Teams iframe + SSO + Backend API integration
        </p>
      </div>
    </div>
  );
};

export default TeamsHello;
